<script setup lang="ts">
import { computed, ref, type Component } from 'vue';
import { useConvexClient, useConvexMutation } from 'convex-vue';
import IconLucideMenu from '~icons/lucide/menu';
import IconLucideMessageSquare from '~icons/lucide/message-square';
import IconLucideMic from '~icons/lucide/mic';
import IconLucidePresentation from '~icons/lucide/presentation';
import IconLucideSquarePen from '~icons/lucide/square-pen';
import IconLucideVideo from '~icons/lucide/video';
import { api } from '../../convex/_generated/api';
import type { ConferenceRoom } from '../convex/useConference';
import { getPermissionLabel } from '../features/permissions';
import CButton from './primitives/CButton.vue';
import CDialog from './primitives/CDialog.vue';
import CInput from './primitives/CInput.vue';
import CMenu from './primitives/CMenu.vue';
import CMenuItem from './primitives/CMenuItem.vue';
import CTeacherIconButton from './primitives/CTeacherIconButton.vue';

type ViewerPermissions = {
  cloakedRoomName: string | null;
  role: 'student' | 'teacher' | 'recorder';
};

type GlobalPermissions = {
  audio: boolean;
  chat: boolean;
  screenshare: boolean;
  video: boolean;
  whiteboard: boolean;
};

const props = defineProps<{
  conferenceSlug: string;
  currentRoomSlug: string;
  globalPermissions: GlobalPermissions;
  hasBreakoutRooms: boolean;
  rooms: ConferenceRoom[];
  viewer: ViewerPermissions;
}>();

const emit = defineEmits<{
  (event: 'notice', payload: { detail?: string; label: string }): void;
}>();

const showRoomControlsMenu = ref(false);
const breakoutBaseName = ref('Room');
const breakoutCount = ref('2');
const breakoutDistribution = ref<'no' | 'distribute' | 'group'>('no');
const includeTeachers = ref(false);
const includeCurrentBoard = ref(false);
const renameRoomsByUsers = ref(false);
const showSendBoardDialog = ref(false);
const selectedBreakoutRoomSlugs = ref<string[]>([]);
const sendingBoardToRooms = ref(false);
const copyingBoardsToMain = ref(false);

const teacherControlsBarClass =
  'flex h-10 items-center justify-between gap-1.5 border-t border-conference-line-soft bg-conference-surface-muted px-1.5';
const roomControlsMenuClass = 'w-62.5 px-0 py-6 text-[10px] text-white';

const convexClient = useConvexClient();
const { mutate: setGlobalPermission } = useConvexMutation(api.permissions.setGlobalPermission);
const { mutate: bringParticipantsToMainRoom } = useConvexMutation(api.participants.bringParticipantsToMainRoom);
const { mutate: createBreakoutRooms } = useConvexMutation(api.rooms.createBreakoutRooms);
const { mutate: closeBreakoutRooms } = useConvexMutation(api.rooms.closeBreakoutRooms);
const { mutate: sendParticipantsBackToAssignedBreakoutRooms } = useConvexMutation(
  api.participants.sendParticipantsBackToAssignedBreakoutRooms,
);
const { mutate: setCloakRoom } = useConvexMutation(api.participants.setCloakRoom);

type PermissionButton = {
  permission: 'audio' | 'video' | 'chat' | 'whiteboard' | 'screenshare';
  active: boolean;
  icon: Component;
  label: string;
};

const permissionButtons = computed(
  () =>
    [
      { permission: 'audio', active: props.globalPermissions.audio, icon: IconLucideMic, label: getPermissionLabel('audio') },
      { permission: 'video', active: props.globalPermissions.video, icon: IconLucideVideo, label: getPermissionLabel('video') },
      {
        permission: 'chat',
        active: props.globalPermissions.chat,
        icon: IconLucideMessageSquare,
        label: getPermissionLabel('chat'),
      },
      {
        permission: 'whiteboard',
        active: props.globalPermissions.whiteboard,
        icon: IconLucideSquarePen,
        label: getPermissionLabel('whiteboard'),
      },
      {
        permission: 'screenshare',
        active: props.globalPermissions.screenshare,
        icon: IconLucidePresentation,
        label: getPermissionLabel('screenshare'),
      },
    ] as PermissionButton[],
);
const openBreakoutRooms = computed(() =>
  props.rooms
    .filter((room) => room.kind === 'breakout' && room.isOpen)
    .sort((left, right) => left.displayName.localeCompare(right.displayName)),
);
const hasOpenBreakoutRooms = computed(() => props.hasBreakoutRooms && openBreakoutRooms.value.length > 0);
const selectedOpenBreakoutRoomSlugs = computed(() => {
  const openSlugs = new Set(openBreakoutRooms.value.map((room) => room.slug));

  return selectedBreakoutRoomSlugs.value.filter((roomSlug) => openSlugs.has(roomSlug));
});

async function togglePermission(
  permission: (typeof permissionButtons.value)[number]['permission'],
  nextValue: boolean,
): Promise<void> {
  try {
    await setGlobalPermission({
      conferenceSlug: props.conferenceSlug,
      permission,
      value: nextValue,
    });
    emit('notice', {
      detail: `${nextValue ? 'Enabled' : 'Disabled'} ${getPermissionLabel(permission)} conference-wide.`,
      label: 'Permission updated',
    });
  } catch (error) {
    emit('notice', {
      detail: error instanceof Error ? error.message : 'Unable to update conference permissions.',
      label: 'Permission update failed',
    });
  }
}

async function toggleCloak(close: () => void): Promise<void> {
  try {
    await setCloakRoom({
      conferenceSlug: props.conferenceSlug,
      roomSlug: props.viewer.cloakedRoomName ? null : props.currentRoomSlug,
    });
    close();
    emit('notice', {
      detail: props.viewer.cloakedRoomName ? 'Teacher cloak cleared.' : 'Teacher cloak enabled for the current room.',
      label: props.viewer.cloakedRoomName ? 'Cloak disabled' : 'Cloak enabled',
    });
  } catch (error) {
    emit('notice', {
      detail: error instanceof Error ? error.message : 'Unable to update cloak state.',
      label: 'Cloak update failed',
    });
  }
}

async function handleCreateBreakoutRooms(close: () => void): Promise<void> {
  const count = Number.parseInt(breakoutCount.value, 10);

  try {
    const result = await createBreakoutRooms({
      baseName: breakoutBaseName.value,
      conferenceSlug: props.conferenceSlug,
      count: Number.isNaN(count) ? 1 : count,
      distributionMode: breakoutDistribution.value,
      includeTeachers: includeTeachers.value,
      renameRoomsByUsers: renameRoomsByUsers.value,
    });
    const createdRoomSlugs = result.createdRooms.map((room) => room.roomSlug);
    let boardCopyError: unknown = null;

    if (includeCurrentBoard.value && createdRoomSlugs.length > 0) {
      try {
        await convexClient.action(api.whiteboards.sendBoardToRooms, {
          conferenceSlug: props.conferenceSlug,
          sourceRoomSlug: props.currentRoomSlug,
          targetRoomSlugs: createdRoomSlugs,
        });
      } catch (error) {
        boardCopyError = error;
      }
    }

    close();
    emit('notice', {
      detail: `${result.createdRooms.map((room) => room.displayName).join(', ')}${
        includeCurrentBoard.value && !boardCopyError ? '; current board sent.' : ''
      }`,
      label: 'Breakout rooms created',
    });

    if (boardCopyError) {
      emit('notice', {
        detail:
          boardCopyError instanceof Error ? boardCopyError.message : 'The rooms were created, but the board could not be sent.',
        label: 'Board send failed',
      });
    }
  } catch (error) {
    emit('notice', {
      detail: error instanceof Error ? error.message : 'Unable to create breakout rooms.',
      label: 'Breakout room creation failed',
    });
  }
}

function openSendBoardToBreakoutRooms(close: () => void): void {
  if (!hasOpenBreakoutRooms.value) {
    emit('notice', {
      detail: 'There are no open breakout rooms available.',
      label: 'No breakout rooms',
    });
    return;
  }

  selectedBreakoutRoomSlugs.value = openBreakoutRooms.value.map((room) => room.slug);
  close();
  showSendBoardDialog.value = true;
}

function selectAllBreakoutRooms(): void {
  selectedBreakoutRoomSlugs.value = openBreakoutRooms.value.map((room) => room.slug);
}

function clearSelectedBreakoutRooms(): void {
  selectedBreakoutRoomSlugs.value = [];
}

async function handleSendBoardToSelectedRooms(close: () => void): Promise<void> {
  const targetRoomSlugs = selectedOpenBreakoutRoomSlugs.value;

  if (targetRoomSlugs.length === 0) {
    emit('notice', {
      detail: 'Choose at least one open breakout room.',
      label: 'No rooms selected',
    });
    return;
  }

  sendingBoardToRooms.value = true;

  try {
    const result = await convexClient.action(api.whiteboards.sendBoardToRooms, {
      conferenceSlug: props.conferenceSlug,
      sourceRoomSlug: props.currentRoomSlug,
      targetRoomSlugs,
    });
    close();
    emit('notice', {
      detail: result.copiedRooms.map((room) => room.displayName).join(', '),
      label: 'Board sent to breakout rooms',
    });
  } catch (error) {
    emit('notice', {
      detail: error instanceof Error ? error.message : 'Unable to send the whiteboard to breakout rooms.',
      label: 'Board send failed',
    });
  } finally {
    sendingBoardToRooms.value = false;
  }
}

async function handleCopyBreakoutBoardsToMain(close: () => void): Promise<void> {
  if (!hasOpenBreakoutRooms.value) {
    emit('notice', {
      detail: 'There are no open breakout rooms to copy back.',
      label: 'No breakout rooms',
    });
    return;
  }

  copyingBoardsToMain.value = true;

  try {
    const result = await convexClient.action(api.whiteboards.copyBreakoutBoardsToMain, {
      conferenceSlug: props.conferenceSlug,
    });
    close();
    emit('notice', {
      detail: `${result.totalInsertedCount} board(s) appended to Main Room.`,
      label: 'Breakout boards copied',
    });
  } catch (error) {
    emit('notice', {
      detail: error instanceof Error ? error.message : 'Unable to copy breakout boards to Main Room.',
      label: 'Breakout board copy failed',
    });
  } finally {
    copyingBoardsToMain.value = false;
  }
}

async function handleCloseBreakoutRooms(close: () => void): Promise<void> {
  try {
    const result = await closeBreakoutRooms({
      conferenceSlug: props.conferenceSlug,
    });
    close();
    emit('notice', {
      detail:
        result.closedRooms.length > 0
          ? `${result.closedRooms.map((room: { displayName: string }) => room.displayName).join(', ')}${
              result.movedParticipantCount > 0
                ? `; returned ${result.movedParticipantCount} participant(s) to the main room.`
                : ''
            }`
          : 'There are no breakout rooms to close.',
      label: result.closedRooms.length > 0 ? 'Breakout rooms closed' : 'No breakout rooms',
    });
  } catch (error) {
    emit('notice', {
      detail: error instanceof Error ? error.message : 'Unable to close breakout rooms.',
      label: 'Breakout room close failed',
    });
  }
}

async function handleBringParticipantsToMainRoom(close: () => void): Promise<void> {
  try {
    const result = await bringParticipantsToMainRoom({
      conferenceSlug: props.conferenceSlug,
    });
    close();
    emit('notice', {
      detail:
        result.movedParticipantIds.length > 0
          ? `Returned ${result.movedParticipantIds.length} participant(s) to ${result.roomDisplayName}.`
          : 'Everyone is already in the main room.',
      label: result.movedParticipantIds.length > 0 ? 'Participants returned' : 'No room change needed',
    });
  } catch (error) {
    emit('notice', {
      detail: error instanceof Error ? error.message : 'Unable to return participants to the main room.',
      label: 'Return to main room failed',
    });
  }
}

async function handleSendParticipantsBackToAssignedBreakoutRooms(close: () => void): Promise<void> {
  try {
    const result = await sendParticipantsBackToAssignedBreakoutRooms({
      conferenceSlug: props.conferenceSlug,
    });
    close();
    emit('notice', {
      detail:
        result.movedParticipantIds.length > 0
          ? `Returned ${result.movedParticipantIds.length} participant(s) to their breakout rooms.`
          : 'No participants have an available breakout room assignment to restore.',
      label: result.movedParticipantIds.length > 0 ? 'Breakout assignments restored' : 'No breakout assignments restored',
    });
  } catch (error) {
    emit('notice', {
      detail: error instanceof Error ? error.message : 'Unable to restore breakout room assignments.',
      label: 'Return to breakout rooms failed',
    });
  }
}

function permissionIndicatorClass(active: boolean): string {
  return active ? 'bg-conference-accent-deep' : 'bg-conference-line-soft';
}
</script>

<template>
  <section v-if="viewer.role === 'teacher'" :class="teacherControlsBarClass" data-testid="conference-teacher-controls-bar">
    <CTeacherIconButton
      v-for="button in permissionButtons"
      :key="button.permission"
      class="relative"
      :active="button.active"
      :aria-pressed="button.active"
      :aria-label="`${button.active ? 'Disable' : 'Enable'} ${button.label}`"
      @click="void togglePermission(button.permission, !button.active)"
    >
      <component :is="button.icon" aria-hidden="true" class="size-4.5" />
      <span class="absolute inset-x-0 bottom-0 h-1" :class="permissionIndicatorClass(button.active)" />
    </CTeacherIconButton>

    <CMenu
      v-model:open="showRoomControlsMenu"
      align="left"
      tone="chrome"
      width="lg"
      panel-test-id="conference-teacher-room-controls-menu"
    >
      <template #trigger="{ toggle }">
        <CTeacherIconButton
          class="ml-auto"
          tone="black"
          data-testid="conference-teacher-room-controls-trigger"
          aria-label="Teacher room controls"
          @click="toggle"
        >
          <IconLucideMenu aria-hidden="true" class="size-4.5" />
        </CTeacherIconButton>
      </template>

      <template #default="{ close }">
        <div :class="roomControlsMenuClass">
          <div class="space-y-px">
            <CMenuItem data-testid="bring-users-main-room" tone="chrome" @click="void handleBringParticipantsToMainRoom(close)">
              Bring Users Back to Main Room
            </CMenuItem>
            <CMenuItem
              data-testid="send-users-breakout-rooms"
              tone="chrome"
              @click="void handleSendParticipantsBackToAssignedBreakoutRooms(close)"
            >
              Send Users Back to Breakout Rooms
            </CMenuItem>
            <CMenuItem
              data-testid="copy-breakout-boards-main-room"
              tone="chrome"
              @click="void handleCopyBreakoutBoardsToMain(close)"
            >
              Copy Boards to Main Room
            </CMenuItem>
            <CMenuItem data-testid="send-board-breakout-rooms" tone="chrome" @click="openSendBoardToBreakoutRooms(close)">
              Send Board to Breakout Rooms
            </CMenuItem>
            <CMenuItem tone="chrome" @click="void handleCloseBreakoutRooms(close)">Close all Breakout Rooms</CMenuItem>
            <CMenuItem tone="chrome" @click="void toggleCloak(close)">Cloak / Uncloak</CMenuItem>
          </div>

          <div class="my-3 border-t border-white/20" />

          <div class="space-y-3 px-3">
            <p class="font-semibold">Create Breakout Room</p>

            <label class="flex flex-col gap-1">
              <span>Room Name</span>
              <CInput v-model="breakoutBaseName" data-testid="breakout-name-input" size="sm" tone="light" />
            </label>

            <label class="flex flex-col gap-1">
              <span>Number / Group Size</span>
              <CInput v-model="breakoutCount" data-testid="breakout-count-input" size="sm" tone="light" />
            </label>

            <div class="space-y-1">
              <label class="flex items-center gap-2">
                <input v-model="breakoutDistribution" data-testid="breakout-distribution-none" type="radio" value="no" />
                <span>Don't move users</span>
              </label>
              <label class="flex items-center gap-2">
                <input
                  v-model="breakoutDistribution"
                  data-testid="breakout-distribution-distribute"
                  type="radio"
                  value="distribute"
                />
                <span>Distribute users equally</span>
              </label>
              <label class="flex items-center gap-2">
                <input v-model="breakoutDistribution" data-testid="breakout-distribution-group" type="radio" value="group" />
                <span>In groups of</span>
              </label>
            </div>

            <label class="flex items-center gap-2">
              <input v-model="includeTeachers" data-testid="breakout-include-teachers" type="checkbox" />
              <span>Include Teachers</span>
            </label>
            <label class="flex items-center gap-2">
              <input v-model="includeCurrentBoard" data-testid="breakout-include-current-board" type="checkbox" />
              <span>Send Current Board</span>
            </label>
            <label class="flex items-center gap-2">
              <input v-model="renameRoomsByUsers" data-testid="breakout-rename-rooms-by-users" type="checkbox" />
              <span>Name Rooms by User(s)</span>
            </label>

            <CButton data-testid="create-breakout-room" size="sm" tone="accent" @click="void handleCreateBreakoutRooms(close)">
              Create
            </CButton>
          </div>
        </div>
      </template>
    </CMenu>

    <CDialog
      v-model:open="showSendBoardDialog"
      panel-test-id="send-board-breakout-dialog"
      title="Send board to breakout rooms"
      width="md"
    >
      <template #default>
        <div class="space-y-2">
          <div class="flex items-center justify-end gap-1">
            <CButton size="sm" tone="subtle" @click="selectAllBreakoutRooms">All</CButton>
            <CButton size="sm" tone="subtle" @click="clearSelectedBreakoutRooms">None</CButton>
          </div>

          <div class="border-conference-line-soft max-h-56 overflow-y-auto border">
            <label
              v-for="room in openBreakoutRooms"
              :key="room.slug"
              class="border-conference-line-soft flex items-center gap-2 border-b px-2 py-1.5 last:border-b-0"
            >
              <input v-model="selectedBreakoutRoomSlugs" type="checkbox" :value="room.slug" />
              <span class="min-w-0 flex-1 truncate">{{ room.displayName }}</span>
            </label>
          </div>
        </div>
      </template>

      <template #footer="{ close }">
        <div class="flex justify-end gap-1.5">
          <CButton size="sm" tone="subtle" @click="close">Cancel</CButton>
          <CButton
            data-testid="confirm-send-board-breakout-rooms"
            size="sm"
            tone="accent"
            :disabled="sendingBoardToRooms || selectedOpenBreakoutRoomSlugs.length === 0"
            @click="void handleSendBoardToSelectedRooms(close)"
          >
            Send
          </CButton>
        </div>
      </template>
    </CDialog>
  </section>
</template>
